import { useEffect, useMemo, useState } from "react";
import { fetchFPLData, fetchClassicLeague, fetchManagerHistory, fetchGwTop, fetchFplLive } from "../api/fplApi";

const CHIP_LABELS = {
  "3xc": "Triple Captain",
  bboost: "Bench Boost",
  freehit: "Free Hit",
  wildcard: "Wildcard",
};

const CACHE_TTL = 10 * 60 * 1000;

const Icons = {
  users: <svg viewBox="0 0 24 24" className="w-6 h-6"><path fill="currentColor" d="M16 11a3 3 0 1 0-3-3a3 3 0 0 0 3 3m-8 0a3 3 0 1 0-3-3a3 3 0 0 0 3 3m0 2c-2.7 0-7 1.35-7 4v2h14v-2c0-2.65-4.3-4-7-4m8 0c-.34 0-.7.02-1.06.05c1.9.87 3.06 2.1 3.06 3.95v2h5v-2c0-2.65-4.3-4-7-4"/></svg>,
  average: <svg viewBox="0 0 24 24" className="w-6 h-6"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="M4 18h16M6 14l4-4l3 3l5-6m0 0v4m0-4h-4"/></svg>,
  trophy: <svg viewBox="0 0 24 24" className="w-6 h-6"><path fill="currentColor" d="M18 2H6v3H2v3c0 2.76 2.24 5 5 5h.18A6 6 0 0 0 11 15.91V19H7v2h10v-2h-4v-3.09A6 6 0 0 0 16.82 13H17c2.76 0 5-2.24 5-5V5h-4V2M7 11a3 3 0 0 1-3-3V7h2v1c0 1.1.3 2.13.82 3H7m13-3a3 3 0 0 1-3 3h-.82A5.96 5.96 0 0 0 18 8V7h2v1Z"/></svg>,
  lowScore: <svg viewBox="0 0 24 24" className="w-6 h-6"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="m4 6l6 6l4-4l6 6m0 0v-5m0 5h-5"/></svg>,
  overall: <svg viewBox="0 0 24 24" className="w-6 h-6"><path fill="currentColor" d="M4 19h4V9H4v10m6 0h4V5h-4v14m6 0h4V12h-4v7Z"/></svg>,
  transfers: <svg viewBox="0 0 24 24" className="w-6 h-6"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M7 7h13l-3-3m3 3l-3 3M17 17H4l3 3m-3-3l3-3"/></svg>,
  hits: <svg viewBox="0 0 24 24" className="w-6 h-6"><path fill="currentColor" d="M12 2a5 5 0 1 1 0 10a5 5 0 0 1 0-10M4 21v-2c0-3.31 3.58-6 8-6c1.13 0 2.2.18 3.16.49L12.65 16H9v2h3.65l2.82 2.82C14.43 21.18 13.25 21.4 12 21H4m15.5-7l1.5 1.5l-5 5l-1.5-1.5l5-5Z"/></svg>,
  deduction: <svg viewBox="0 0 24 24" className="w-6 h-6"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="M5 12h14"/><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2"/></svg>,
  chipsTotal: <svg viewBox="0 0 24 24" className="w-6 h-6"><path fill="currentColor" d="M12 2a10 10 0 1 0 10 10h-2A8 8 0 1 1 12 4z"/></svg>,
  crown: <svg viewBox="0 0 24 24" className="w-5 h-5"><path fill="currentColor" d="M2 7l4 3l3-5l3 5l3-5l3 5l4-3v10H2zM4 15h16v2H4z"/></svg>,
  bench: <svg viewBox="0 0 24 24" className="w-5 h-5"><path fill="currentColor" d="M3 10h18v2H3v-2m2 4h14v2H5v-2m-2 4h2v2H3v-2m16 0h2v2h-2v-2"/></svg>,
  wand: <svg viewBox="0 0 24 24" className="w-5 h-5"><path fill="currentColor" d="m17.66 3.93l1.41 1.41l-5.66 5.66l-1.41-1.41l5.66-5.66M2 22l9-3l-6-6l-3 9m13-7l2-2l1.5 1.5l-2 2L15 15m4-9l2-2l1.5 1.5l-2 2L19 6"/></svg>,
  card: <svg viewBox="0 0 24 24" className="w-5 h-5"><path fill="currentColor" d="M4 5h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2m0 2v2h16V7H4m0 6v4h16v-4H4z"/></svg>,
  spinner: <svg viewBox="0 0 24 24" className="w-6 h-6 animate-spin"><path fill="currentColor" d="M12 2a10 10 0 1 0 10 10h-2A8 8 0 1 1 12 4z"/></svg>,
};

export default function LeagueStats({ leagueId = 136323, title = "S16", embedded = false }) {
  const [gw, setGw] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [totals, setTotals] = useState({
    managers: 0,
    usedAny: 0,
    chips: { "3xc": 0, bboost: 0, freehit: 0, wildcard: 0 },
    chipManagers: { "3xc": [], bboost: [], freehit: [], wildcard: [] },
    avgGw: 0,
    highestGw: 0,
    lowestGw: 0,
    highestManager: null,
    lowestManager: null,
    avgOverall: 0,
    totalTransfers: 0,
    managersWithHits: 0,
    totalHitPoints: 0,
    hitManagers: [],
  });
  const [topCaptains, setTopCaptains] = useState([]);
  const [teamPopup, setTeamPopup] = useState(null);
  const [selectedChip, setSelectedChip] = useState(null);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError, setTeamError] = useState("");
  const [showHitManagers, setShowHitManagers] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchFPLData();
        const now = Date.now();
        const latest = (data?.events || [])
          .filter((e) => {
            const deadline = new Date(e.deadline_time).getTime();
            return e.finished || e.data_checked || (Number.isFinite(deadline) && deadline <= now);
          })
          .sort((a, b) => b.id - a.id)[0]?.id || 1;
        setGw(latest);
      } catch { setGw(1); }
    })();
  }, []);

  useEffect(() => {
    setTopCaptains([]);
    setTotals((prev) => ({ ...prev, managers: 0 }));
  }, [leagueId]);

  const gwOptions = useMemo(() => Array.from({ length: 38 }, (_, i) => i + 1), []);
  const cacheKey = `league-stats-v63-${leagueId}-${gw}`;

  const applyCached = (cached) => {
    setTotals(cached.totals);
    setTopCaptains(cached.topCaptains || []);
  };

  const load = async ({ force = false } = {}) => {
    setErr("");

    if (!force) {
      try {
        const raw = sessionStorage.getItem(cacheKey);
        if (raw) {
          const cached = JSON.parse(raw);
          if (Date.now() - cached.savedAt < CACHE_TTL) {
            applyCached(cached);
            return;
          }
        }
      } catch (_) {}
    }

    setLoading(true);
    try {
      const standings = await fetchClassicLeague(leagueId, gw);
      const managers = Array.isArray(standings) ? standings.length : 0;
      const gwPerformances = [];
      const overallScores = [];

      const chips = { "3xc": 0, bboost: 0, freehit: 0, wildcard: 0 };
      const chipManagers = { "3xc": [], bboost: [], freehit: [], wildcard: [] };
      let usedAny = 0;
      let totalTransfers = 0;
      let managersWithHits = 0;
      let totalHitPoints = 0;
      const hitManagers = [];

      const batchSize = 20;
      for (let i = 0; i < standings.length; i += batchSize) {
        const batch = standings.slice(i, i + batchSize);
        const histories = await Promise.all(batch.map((row) => fetchManagerHistory(row.id)));
        for (let historyIndex = 0; historyIndex < histories.length; historyIndex += 1) {
          const hist = histories[historyIndex];
          const manager = batch[historyIndex];
          if (!hist) continue;

          const used = hist?.chips?.find((c) => c.event === gw)?.name || null;
          if (used) {
            usedAny += 1;
            if (chips[used] !== undefined) {
              chips[used] += 1;
              chipManagers[used].push({
                id: manager.id,
                managerName: manager.manager_name,
                teamName: manager.team_name,
              });
            }
          }

          const event = hist?.current?.find((row) => Number(row.event) === Number(gw));
          if (event) {
            const grossPoints = Number(event.points);
            const transfers = Number(event.event_transfers) || 0;
            const hit = Number(event.event_transfers_cost) || 0;
            const selectedGwOverall = Number(event.total_points);

            totalTransfers += transfers;
            totalHitPoints += hit;
            if (hit > 0) {
              managersWithHits += 1;
              hitManagers.push({
                id: manager.id,
                managerName: manager.manager_name,
                teamName: manager.team_name,
                hitPoints: hit,
                transfers,
              });
            }

            if (Number.isFinite(grossPoints)) {
              gwPerformances.push({
                score: grossPoints - hit,
                managerId: manager.id,
                managerName: manager.manager_name,
                teamName: manager.team_name,
              });
            }
            if (Number.isFinite(selectedGwOverall)) overallScores.push(selectedGwOverall);
          }
        }
      }

      const sortedPerformances = [...gwPerformances].sort((a, b) => b.score - a.score);
      const highest = sortedPerformances[0] || null;
      const lowest = sortedPerformances[sortedPerformances.length - 1] || null;

      const nextTotals = {
        managers,
        usedAny,
        chips,
        chipManagers,
        avgGw: gwPerformances.length
          ? Math.round(gwPerformances.reduce((sum, row) => sum + row.score, 0) / gwPerformances.length)
          : 0,
        highestGw: highest?.score ?? 0,
        lowestGw: lowest?.score ?? 0,
        highestManager: highest,
        lowestManager: lowest,
        avgOverall: overallScores.length
          ? Math.round(overallScores.reduce((sum, score) => sum + score, 0) / overallScores.length)
          : 0,
        totalTransfers,
        managersWithHits,
        totalHitPoints,
        hitManagers: hitManagers.sort((a, b) => b.hitPoints - a.hitPoints || String(a.managerName || "").localeCompare(String(b.managerName || ""))),
      };

      const gwTop = await fetchGwTop(leagueId, gw);
      const rows = [...(gwTop?.top3 || []), ...(gwTop?.others || [])];
      const freq = new Map();
      rows.forEach((r) => {
        if (!r?.captain_id) return;
        const cur = freq.get(r.captain_id) || { name: r.captain_name, code: r.captain_code, count: 0 };
        cur.count += 1;
        if (!cur.name) cur.name = r.captain_name;
        if (!cur.code) cur.code = r.captain_code;
        freq.set(r.captain_id, cur);
      });

      const nextCaptains = Array.from(freq.entries())
        .map(([id, info]) => ({
          id,
          name: info.name || `#${id}`,
          code: info.code || null,
          count: info.count,
          percent: managers > 0 ? Math.round((info.count / managers) * 100) : 0,
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 3);

      setTotals(nextTotals);
      setTopCaptains(nextCaptains);
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify({ savedAt: Date.now(), totals: nextTotals, topCaptains: nextCaptains }));
      } catch (_) {}
    } catch {
      setErr("Could not load league stats. Please try again.");
      setTopCaptains([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (gw) load(); }, [gw, leagueId]); // eslint-disable-line react-hooks/exhaustive-deps

  const headshotPrimary = (code) => code ? `https://resources.premierleague.com/premierleague25/photos/players/110x140/${code}.png` : null;
  const headshotLegacy = (code) => code ? `https://resources.premierleague.com/premierleague/photos/players/250x250/p${code}.png` : null;

  const openTeam = async (manager) => {
    setTeamPopup({ manager, data: null });
    setTeamLoading(true);
    setTeamError("");
    try {
      const data = await fetchFplLive(manager.id, gw);
      if (data?.error) throw new Error(data.error);
      setTeamPopup({ manager, data });
    } catch {
      setTeamError(`Could not load this manager's GW${gw} team.`);
    } finally {
      setTeamLoading(false);
    }
  };

  return (
    <div className={`${embedded ? "p-1 sm:p-2" : "max-w-6xl mx-auto px-4 py-6"}`}>
      <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          {!embedded && <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-800">{title} League Stats</h1>}
          <p className="text-sm text-slate-500 mt-1">Net Gameweek performance, chip usage, transfers and captain trends.</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <label className="text-sm font-medium text-slate-700">Gameweek</label>
          <select className="border rounded-xl px-3 py-2 shadow-sm focus:outline-none bg-white" value={gw ?? ""} onChange={(e) => setGw(Number(e.target.value))} disabled={!gw}>
            {!gw && <option value="">Loading…</option>}
            {gwOptions.map((g) => <option key={g} value={g}>GW {g}</option>)}
          </select>
          <button onClick={() => load({ force: true })} className="px-4 py-2 rounded-xl border shadow-sm bg-slate-100 text-slate-800 hover:bg-slate-200 transition inline-flex items-center gap-2" disabled={loading || !gw}>
            {loading ? (<>{Icons.spinner}<span>Loading…</span></>) : "Refresh"}
          </button>
        </div>
      </div>

      {err && <div className="text-red-600 mb-4">{err}</div>}
      {loading && <div className="mb-4 flex items-center gap-2 text-slate-600">{Icons.spinner}<span>Fetching {title} stats…</span></div>}

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4 mb-5">
        <StatCard title="Managers" value={totals.managers} icon={Icons.users} theme="violet" />
        <StatCard title="Average GW Score" value={totals.avgGw} icon={Icons.average} theme="blue" />
        <StatCard
          title="Highest GW Score"
          value={totals.highestGw}
          icon={Icons.trophy}
          theme="emerald"
          subtitle={totals.highestManager ? `${totals.highestManager.managerName} · ${totals.highestManager.teamName}` : ""}
        />
        <StatCard
          title="Lowest GW Score"
          value={totals.lowestGw}
          icon={Icons.lowScore}
          theme="rose"
          subtitle={totals.lowestManager ? `${totals.lowestManager.managerName} · ${totals.lowestManager.teamName}` : ""}
        />
        <StatCard title="Average Overall Points" value={totals.avgOverall} icon={Icons.overall} theme="cyan" />
        <StatCard title="Transfers Made" value={totals.totalTransfers} icon={Icons.transfers} theme="indigo" />
        <StatCard title="Managers Taking Hits" value={totals.managersWithHits} icon={Icons.hits} theme="amber" onClick={() => setShowHitManagers(true)} action="View managers" />
        <StatCard title="Transfer Hit Points" value={totals.totalHitPoints} icon={Icons.deduction} theme="orange" />
      </div>

      <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-white via-violet-50/40 to-cyan-50/50 ring-1 ring-slate-200 shadow-sm p-4 sm:p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-extrabold text-slate-900">Chip Usage</div>
            <div className="text-xs text-slate-500">GW {gw} · {totals.usedAny} managers used a chip</div>
          </div>
          <span className="rounded-xl bg-violet-100 p-2 text-violet-700">{Icons.chipsTotal}</span>
        </div>
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          <ChipCard label={CHIP_LABELS["3xc"]} count={totals.chips["3xc"]} onClick={() => setSelectedChip("3xc")} icon={Icons.crown} theme="amber" />
          <ChipCard label={CHIP_LABELS.bboost} count={totals.chips.bboost} onClick={() => setSelectedChip("bboost")} icon={Icons.bench} theme="blue" />
          <ChipCard label={CHIP_LABELS.freehit} count={totals.chips.freehit} onClick={() => setSelectedChip("freehit")} icon={Icons.wand} theme="violet" />
          <ChipCard label={CHIP_LABELS.wildcard} count={totals.chips.wildcard} onClick={() => setSelectedChip("wildcard")} icon={Icons.card} theme="emerald" />
        </div>
      </div>

      {topCaptains.length > 0 && (
        <div className="mt-7">
          <div className="text-base font-extrabold text-slate-800 mb-4">Top Captain Picks · GW {gw}</div>
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-3">
            {topCaptains.map((p, idx) => (
              <TopCaptainCard key={p.id ?? idx} rank={idx + 1} name={p.name} count={p.count} percent={p.percent} primaryImg={headshotPrimary(p.code)} legacyImg={headshotLegacy(p.code)} />
            ))}
          </div>
        </div>
      )}

      <p className="mt-6 text-xs text-slate-500">GW scores are shown after transfer-hit deductions. Failed or unavailable manager histories are excluded from score calculations.</p>
      {selectedChip && <ChipManagersPopup chip={selectedChip} gw={gw} managers={totals.chipManagers?.[selectedChip] || []} onManagerClick={(manager) => { setSelectedChip(null); openTeam(manager); }} onClose={() => setSelectedChip(null)} />}
      {showHitManagers && <HitManagersPopup gw={gw} managers={totals.hitManagers || []} total={totals.totalHitPoints} onManagerClick={(manager) => { setShowHitManagers(false); openTeam(manager); }} onClose={() => setShowHitManagers(false)} />}
      {teamPopup && <TeamPopup popup={teamPopup} gw={gw} loading={teamLoading} error={teamError} onClose={() => setTeamPopup(null)} />}
    </div>
  );
}

function StatCard({ title, value, icon, subtitle = "", theme = "violet", onClick, action }) {
  const themes = {
    violet: "from-violet-500 to-fuchsia-500 bg-violet-50 text-violet-700",
    blue: "from-blue-500 to-cyan-500 bg-blue-50 text-blue-700",
    emerald: "from-emerald-500 to-teal-500 bg-emerald-50 text-emerald-700",
    rose: "from-rose-500 to-pink-500 bg-rose-50 text-rose-700",
    cyan: "from-cyan-500 to-sky-500 bg-cyan-50 text-cyan-700",
    indigo: "from-indigo-500 to-violet-500 bg-indigo-50 text-indigo-700",
    amber: "from-amber-500 to-yellow-500 bg-amber-50 text-amber-700",
    orange: "from-orange-500 to-red-500 bg-orange-50 text-orange-700",
  };
  const style = themes[theme] || themes.violet;
  const Tag = onClick ? "button" : "div";
  return (
    <Tag type={onClick ? "button" : undefined} onClick={onClick} className={`group relative overflow-hidden rounded-2xl p-4 sm:p-5 shadow-sm bg-white text-left ring-1 ring-slate-200 min-w-0 transition duration-200 hover:-translate-y-0.5 hover:shadow-md ${onClick ? "cursor-pointer focus:outline-none focus:ring-2 focus:ring-violet-400" : ""}`}>
      <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${style.split(" ").slice(0,2).join(" ")}`} />
      <div className="flex items-center justify-between gap-2">
        <div className="text-slate-500 text-xs sm:text-sm font-semibold">{title}</div>
        {icon && <span className={`shrink-0 rounded-xl p-2 ${style.split(" ").slice(2).join(" ")}`}>{icon}</span>}
      </div>
      <div className="text-2xl sm:text-3xl font-black mt-2 text-slate-900">{value}</div>
      {subtitle && <div className="mt-2 text-[11px] leading-snug font-semibold text-slate-500 truncate" title={subtitle}>{subtitle}</div>}
      {action && <div className="mt-2 text-[10px] font-black uppercase tracking-wide text-violet-700">{action} →</div>}
    </Tag>
  );
}

function ChipCard({ label, count, onClick, icon, theme = "violet" }) {
  const themes = {
    amber: "bg-amber-100 text-amber-700 ring-amber-200",
    blue: "bg-blue-100 text-blue-700 ring-blue-200",
    violet: "bg-violet-100 text-violet-700 ring-violet-200",
    emerald: "bg-emerald-100 text-emerald-700 ring-emerald-200",
  };
  return (
    <button type="button" onClick={onClick} className="rounded-xl p-4 bg-white/90 text-left ring-1 ring-slate-200 transition duration-200 hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-violet-400">
      <div className="flex items-center gap-3">
        <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ring-1 ${themes[theme]}`}>{icon}</span>
        <div className="min-w-0"><div className="truncate text-xs sm:text-sm font-bold text-slate-600">{label}</div><div className="text-2xl font-black leading-tight text-slate-900">{count}</div></div>
      </div>
      <div className="mt-3 text-[10px] font-black uppercase tracking-wide text-violet-700">View managers →</div>
    </button>
  );
}

function ChipManagersPopup({ chip, gw, managers, onManagerClick, onClose }) {
  return <div className="fixed inset-0 z-[115] flex items-end justify-center bg-slate-950/65 p-0 backdrop-blur-sm sm:items-center sm:p-5" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
    <div className="max-h-[85vh] w-full max-w-lg overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl">
      <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-5 py-4">
        <div><div className="text-[10px] font-black uppercase tracking-[.14em] text-violet-700">GW{gw} chip users</div><h3 className="text-xl font-black text-slate-950">{CHIP_LABELS[chip]}</h3><p className="text-xs text-slate-500">{managers.length} {managers.length === 1 ? "manager" : "managers"}</p></div>
        <button type="button" onClick={onClose} className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-black text-slate-700 hover:bg-slate-50">Close</button>
      </div>
      <div className="max-h-[65vh] overflow-y-auto p-4">
        {managers.length ? <div className="space-y-2">{managers.map((manager) => <button key={manager.id} type="button" onClick={() => onManagerClick(manager)} className="group flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-left transition hover:border-violet-300 hover:bg-violet-50">
          <span className="min-w-0"><span className="block truncate text-sm font-black text-slate-900">{manager.managerName}</span><span className="block truncate text-xs text-slate-500">{manager.teamName}</span></span>
          <span className="shrink-0 text-xs font-black text-violet-700 group-hover:underline">View team →</span>
        </button>)}</div> : <div className="py-12 text-center text-sm font-semibold text-slate-400">No managers used this chip in GW{gw}.</div>}
      </div>
    </div>
  </div>;
}

function HitManagersPopup({ gw, managers, total, onManagerClick, onClose }) {
  return <div className="fixed inset-0 z-[115] flex items-end justify-center bg-slate-950/65 p-0 backdrop-blur-sm sm:items-center sm:p-5" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
    <div className="max-h-[85vh] w-full max-w-lg overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl">
      <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-5 py-4"><div><div className="text-[10px] font-black uppercase tracking-[.14em] text-orange-600">GW{gw} transfer deductions</div><h3 className="text-xl font-black text-slate-950">Managers Taking Hits</h3><p className="text-xs text-slate-500">{managers.length} {managers.length === 1 ? "manager" : "managers"} · -{total} points combined</p></div><button type="button" onClick={onClose} className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-black text-slate-700 hover:bg-slate-50">Close</button></div>
      <div className="max-h-[65vh] overflow-y-auto p-4">{managers.length ? <div className="space-y-2">{managers.map((manager) => <button key={manager.id} type="button" onClick={() => onManagerClick(manager)} className="group flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-left transition hover:border-orange-300 hover:bg-orange-50"><span className="min-w-0"><span className="block truncate text-sm font-black text-slate-900">{manager.managerName}</span><span className="block truncate text-xs text-slate-500">{manager.teamName} · {manager.transfers} transfers</span></span><span className="shrink-0 text-right"><span className="block text-sm font-black text-rose-600">-{manager.hitPoints} pts</span><span className="text-[10px] font-black text-violet-700 group-hover:underline">View team →</span></span></button>)}</div> : <div className="py-12 text-center text-sm font-semibold text-slate-400">No managers took a transfer hit in GW{gw}.</div>}</div>
    </div>
  </div>;
}

function TeamPopup({ popup, gw, loading, error, onClose }) {
  const players = popup.data?.squad || popup.data?.players || popup.data?.picks || [];
  const starters = players.filter((p) => Number(p.position || 0) <= 11 && !p.is_bench);
  const bench = players.filter((p) => Number(p.position || 0) > 11 || p.is_bench);
  const rows = [1, 2, 3, 4].map((type) => starters.filter((p) => Number(p.element_type) === type));
  const points = popup.data?.totals?.total_after_hits ?? popup.data?.entry_history?.points;
  const chip = popup.data?.active_chip;
  return <div className="fixed inset-0 z-[120] flex items-end justify-center bg-slate-950/65 p-0 backdrop-blur-sm sm:items-center sm:p-5" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
    <div className="max-h-[94vh] w-full max-w-3xl overflow-y-auto rounded-t-3xl bg-slate-100 shadow-2xl sm:rounded-3xl">
      <div className="sticky top-0 z-20 flex items-center justify-between gap-4 border-b border-slate-200 bg-white px-4 py-4 sm:px-6">
        <div className="min-w-0"><div className="text-[10px] font-black uppercase tracking-[.14em] text-violet-700">GW{gw} team</div><h3 className="truncate text-xl font-black text-slate-950">{popup.manager.teamName || popup.manager.managerName}</h3><p className="truncate text-xs text-slate-500">{popup.manager.managerName}{points != null ? ` · ${points} points` : ""}{chip ? ` · ${CHIP_LABELS[chip] || chip}` : ""}</p></div>
        <button type="button" onClick={onClose} className="shrink-0 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-black text-slate-700 hover:bg-slate-50">Close</button>
      </div>
      {loading ? <div className="flex min-h-72 items-center justify-center gap-3 text-sm font-bold text-violet-700">{Icons.spinner} Loading team…</div> : error ? <div className="m-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div> : <div className="p-3 sm:p-5">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-b from-emerald-500 to-emerald-700 px-2 pb-4 pt-5 shadow-lg sm:px-5">
          <div className="pointer-events-none absolute inset-3 rounded-xl border-2 border-white/35" />
          <div className="relative space-y-5 sm:space-y-6">{rows.map((row, index) => row.length > 0 && <div key={index} className="flex min-h-20 items-start justify-around gap-1">{row.map((p) => <PopupPlayer key={p.id || p.element} player={p} chip={chip} />)}</div>)}</div>
          <div className="relative mt-5 rounded-xl bg-slate-950/25 p-2"><div className="mb-2 text-center text-[10px] font-black uppercase tracking-widest text-white/80">Substitutes</div><div className="flex justify-around gap-1">{bench.map((p) => <PopupPlayer key={p.id || p.element} player={p} chip={chip} bench />)}</div></div>
        </div>
        <a href={`https://fantasy.premierleague.com/entry/${popup.manager.id}/event/${gw}`} target="_blank" rel="noreferrer" className="mt-4 flex w-full items-center justify-center rounded-xl bg-violet-700 px-4 py-3 text-sm font-black text-white hover:bg-violet-800">Open on official FPL ↗</a>
      </div>}
    </div>
  </div>;
}

function PopupPlayer({ player, chip, bench = false }) {
  const name = player.name || player.web_name || "Player";
  const isCaptain = Boolean(player.is_captain || player.captain);
  const isVice = Boolean(player.is_vice || player.is_vice_captain);
  const multiplier = isCaptain ? (chip === "3xc" ? 3 : 2) : Number(player.multiplier || 1);
  const rawPoints = Number(player.points ?? player.total_points ?? player.gwPoints ?? 0);
  const points = rawPoints * Math.max(1, multiplier);
  const teamCode = player.team_code;
  return <div className={`relative w-[64px] text-center sm:w-[82px] ${bench ? "opacity-95" : ""}`}>
    <div className="relative mx-auto h-12 w-12 sm:h-16 sm:w-16">{teamCode ? <img src={`https://fantasy.premierleague.com/dist/img/shirts/standard/shirt_${teamCode}-220.webp`} alt="" className="h-full w-full object-contain drop-shadow-md" /> : <div className="h-full w-full rounded-lg bg-white/20" />}{(isCaptain || isVice) && <span className="absolute -right-1 -top-1 grid h-5 w-5 place-items-center rounded-full bg-slate-950 text-[9px] font-black text-white">{isCaptain ? "C" : "V"}</span>}</div>
    <div className="truncate rounded-t-md bg-slate-950 px-1 py-1 text-[8px] font-bold text-white sm:text-[10px]" title={name}>{name}</div>
    <div className="rounded-b-md bg-white px-1 py-1 text-[8px] font-black text-slate-900 sm:text-[10px]">{points} pts</div>
  </div>;
}

function TopCaptainCard({ rank, name, count, percent, primaryImg, legacyImg }) {
  const rankColors = ["bg-yellow-100 text-yellow-800 ring-yellow-200", "bg-slate-100 text-slate-700 ring-slate-200", "bg-amber-50 text-amber-800 ring-amber-200"];
  const badgeStyle = rankColors[Math.min(rank - 1, 2)];
  return (
    <div className="rounded-2xl p-5 shadow-sm bg-white ring-1 ring-slate-200 flex items-center gap-4">
      <div className="w-[76px] h-[92px] overflow-hidden rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
        {primaryImg ? (
          <img src={primaryImg} alt={name} className="w-full h-full object-cover" loading="lazy" onError={(e) => {
            if (legacyImg && e.currentTarget.src !== legacyImg) e.currentTarget.src = legacyImg;
            else e.currentTarget.style.display = "none";
          }} />
        ) : <span className="text-slate-400 text-xs">No Image</span>}
      </div>
      <div className="min-w-0">
        <div className={`inline-flex px-2.5 py-1 text-xs font-bold rounded-full ring-1 mb-2 ${badgeStyle}`}>#{rank}</div>
        <div className="text-lg font-black text-slate-900 truncate">{name}</div>
        <div className="text-slate-500 text-sm mt-1"><span className="font-bold text-slate-700">{count}</span> captains{percent > 0 ? ` · ${percent}%` : ""}</div>
      </div>
    </div>
  );
}
